import { dictionaryApi } from './dictionaryClient';
import { type Barna, normalizeBarna } from './barnaApi';
import { normalizeBhedBikalpa, type BhedBikalpa } from './bhedBikalpaApi';

export type MatraType = 'hraswa' | 'dirgha' | 'pluta';
export type SworType = 'udatta' | 'anudatta' | 'swarit';
export type AnunasikType = 'anunasik' | 'ananunasik';

export type Bhed = {
  id: number;
  barnaId: number;
  matra: MatraType;
  swor: SworType;
  anunasik: AnunasikType;
  text: string;
  bikalpas?: BhedBikalpa[];
  barna?: Barna | null;
};

export type CreateBhedBody = {
  barnaId: number;
  matra: MatraType;
  swor: SworType;
  anunasik: AnunasikType;
  text: string;
};

export type UpdateBhedBody = Partial<CreateBhedBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function bikalpaListFromRaw(raw: Record<string, unknown>): unknown[] {
  const list = raw.bhed_bikalpa ?? raw.bhedBikalpas ?? raw.bikalpas;
  return Array.isArray(list) ? list : [];
}

export function normalizeBhed(raw: unknown): Bhed | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  const barnaId = toNum(raw.barnaId ?? raw.barna_id);
  if (id == null || barnaId == null) return null;
  const matra = toStr(raw.matra) as MatraType;
  const swor = toStr(raw.swor) as SworType;
  const anunasik = toStr(raw.anunasik) as AnunasikType;
  const bikalpas = bikalpaListFromRaw(raw)
    .map(normalizeBhedBikalpa)
    .filter((x): x is BhedBikalpa => x != null);
  return {
    id,
    barnaId,
    matra: matra === 'hraswa' || matra === 'dirgha' || matra === 'pluta' ? matra : 'hraswa',
    swor: swor === 'udatta' || swor === 'anudatta' || swor === 'swarit' ? swor : 'udatta',
    anunasik: anunasik === 'anunasik' || anunasik === 'ananunasik' ? anunasik : 'anunasik',
    text: toStr(raw.text),
    ...(bikalpas.length > 0 ? { bikalpas } : {}),
    barna: raw.barna ? normalizeBarna(raw.barna) : null,
  };
}

function normalizeList(data: unknown): Bhed[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeBhed).filter((x): x is Bhed => x != null);
}

export async function listBhed(): Promise<Bhed[]> {
  const res = await dictionaryApi.get<unknown>('/bhed');
  return normalizeList(res.data);
}

export async function getBhed(id: number): Promise<Bhed> {
  const res = await dictionaryApi.get<unknown>(`/bhed/${id}`);
  const row = normalizeBhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed response');
  return row;
}

export async function createBhed(body: CreateBhedBody): Promise<Bhed> {
  const res = await dictionaryApi.post<unknown>('/bhed', body);
  const row = normalizeBhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed response');
  return row;
}

export async function updateBhed(id: number, body: UpdateBhedBody): Promise<Bhed> {
  const res = await dictionaryApi.patch<unknown>(`/bhed/${id}`, body);
  const row = normalizeBhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bhed response');
  return row;
}

export async function deleteBhed(id: number): Promise<void> {
  await dictionaryApi.delete(`/bhed/${id}`);
}
